import React, { useEffect, useCallback, useMemo } from "react";
import { Box, Chip, MenuItem } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import colors from "../../../shared/themes/colors";
import {
  PublicState,
  deleteServiceReport,
  getMachineParts,
  getMachine,
  getServiceReport,
  getServiceReports,
  selectServiceReport
} from "../../publicSlice";
import Moment from "react-moment";
import { ServiceReport, ServiceReportFileTypeCode, ServiceReportStatusCode } from "../../models/serviceReportsModels";
import PriceFormat from "../../../shared/components/formats/PriceFormat";
import { ServiceReportCode } from "../../../shared/ServiceReportStatusCode";
import ReportFile from "../../../shared/ReportFile";
import PaperWithCrossIcon from "../../../shared/assets/icons/PaperWithCrossIcon";
import DownloadIcon from "../../../shared/assets/icons/DownloadIcon";
import publicServiceReportsApi from "../../api/publicServiceReportsApi";
import Loader from "../../../shared/components/Loader";
import useTranslation from "../../../shared/hooks/useTranslation";
import usePermission from "../../../shared/hooks/usePermission";
import { Modules } from "../../../core/models/PermissionModels";
import KioskBackButton from "../kiosk/KioskBackButton";

const URGENCY_MAP: Record<number, { label: string; color: string }> = {
  1: { label: "urgencyUrgent", color: "#C0392B" },
  2: { label: "urgencyHigh", color: "#E67E22" },
  4: { label: "urgencyLow", color: "#27AE60" },
};

const ServiceReportPage = () => {
  const { serviceReport, machine, machineParts, loading } = useAppSelector<PublicState>(
    (state: any) => state.public
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirm = useConfirmation();
  const { translate } = useTranslation(["public", "servicing"]);
  const { translate: tCommon } = useTranslation("common");
  // SEC-REQ-3 (KV2-0): mirror the backend permission gates so a Viewer never sees an
  // action that would 403. Edit + Solve require Write, Delete requires Admin. Edit is
  // gated on canWrite (not creator-or-write) because the backend PUT requires Write.
  const { canWrite, canAdmin } = usePermission(Modules.Machinery);

  const { id } = useParams();

  const reportFiles = useMemo(() =>
    serviceReport?.files.filter((file) => file.typeCode === ServiceReportFileTypeCode.reportDocument),
    [serviceReport?.files]
  );

  const solvedReportFiles = useMemo(() =>
    serviceReport?.files.filter((x) => x.typeCode === ServiceReportFileTypeCode.solvingReportDocument),
    [serviceReport?.files]
  );

  const partName = useMemo(() => {
    if (!serviceReport?.affectedPartCategoryId) return undefined;
    if (machineParts && machineParts.length > 0) {
      for (const group of machineParts) {
        const found = group.parts.find((p) => p.id === serviceReport.affectedPartCategoryId);
        if (found) {
          const translationKey = found.code.replace(/_([a-z])/g, (_m: string, c: string) => c.toUpperCase());
          return translate(`servicing:machineParts.parts.${translationKey}`);
        }
      }
    }
    return undefined;
  }, [machineParts, serviceReport?.affectedPartCategoryId, translate]);

  const handleDownloadPdf = useCallback((item: ServiceReport) => {
    publicServiceReportsApi.getServiceReportPdfLink(item.number, `service-report-${item.number}.pdf`);
  }, []);

  const handleEditServiceReport = useCallback((item?: ServiceReport) => {
    if (item) {
      dispatch(selectServiceReport(item));
      navigate(`/public/service-reports/edit/${item.number}`);
    }
  }, [dispatch, navigate]);

  const handleMarkAsSolved = useCallback((item?: ServiceReport) => {
    if (item) {
      dispatch(selectServiceReport(item));
      navigate(`/public/service-reports/solving`);
    }
  }, [dispatch, navigate]);

  const handleDeleteServiceReport = useCallback(async (item?: ServiceReport) => {
    if (item && machine) {
      await dispatch(deleteServiceReport(item.number));
      await dispatch(getServiceReports({ machineNumber: machine.internalNumber, query: { statusCodes: ["InProgress", "Solved"] } }));
      navigate(`/public/machines/${machine.internalNumber}`);
    }
  }, [dispatch, navigate, machine]);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;

      try {
        const machinePartsPromise = dispatch(getMachineParts());

        await dispatch(getServiceReport(id));

        await machinePartsPromise;
      } catch (error) { }
    };

    loadData();
  }, [id, dispatch]);

  useEffect(() => {
    if (serviceReport?.machineNumber) {
      dispatch(getMachine(serviceReport.machineNumber));
    }
  }, [serviceReport?.machineNumber, dispatch]);

  // Customer data is no longer displayed on this page —
  // it's accessible from the dedicated Company Details page on the kiosk dashboard.

  if (loading && !serviceReport) {
    return <Loader />;
  }

  if (!serviceReport) {
    return null;
  }

  return (
    <Box className="v-panel gap-6" sx={{ px: 2, pb: "80px" }}>
      <Box className="heading-2">
        {serviceReport?.statusCode === ServiceReportStatusCode.inProgress
          ? translate("openServiceReport")
          : translate("solvedServiceReport")}
      </Box>

      <Box
        className="w-full p-4 rounded-lg"
        sx={{ background: colors.secondary.gray98, border: `1px solid ${colors.secondary.gray90}` }}
      >
        <Box className="grid grid-cols-2 gap-6">
          <Box className="md:flex md:items-center md:gap-2">
            <Box className="text-14-medium" color={colors.secondary.gray70}>
              {translate("reportNo")}
            </Box>
            <Box className="text-16-medium" color="#333333">
              {serviceReport?.number}
            </Box>
          </Box>

          <Box className="md:flex md:items-center md:gap-2">
            <Box className="text-14-medium" color={colors.secondary.gray70}>
              {translate("openDate")}
            </Box>
            <Box className="text-16-medium" color="#333333">
              <Moment date={serviceReport?.openDate} format="DD.MM.YYYY, HH:mm" />
            </Box>
          </Box>
        </Box>

        <Box className="grid grid-cols-2 gap-6 mt-2">
          <Box className="md:flex md:items-center md:gap-2">
            <Box className="text-14-medium" color={colors.secondary.gray70}>
              {translate("cost")}
            </Box>
            <Box sx={{ minHeight: "24px" }}>
              <PriceFormat value={serviceReport?.cost?.value} />
            </Box>
          </Box>

          <Box className="md:flex md:items-center md:gap-2">
            <Box className="text-14-medium" color={colors.secondary.gray70}>
              {translate("closeDate")}
            </Box>
            {serviceReport?.closeDate && (
              <Box className="text-16-medium" color="#333333" sx={{ minHeight: "24px" }}>
                <Moment date={serviceReport.closeDate} format="DD.MM.YYYY, HH:mm" />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        className="w-full p-4 rounded-lg"
        sx={{ background: colors.secondary.gray98, border: `1px solid ${colors.secondary.gray90}` }}
      >
        <Box className="flex justify-between items-center">
          {serviceReport?.statusCode && (
            <Box sx={{ width: "90px" }}>
              <ServiceReportCode sizeCode={serviceReport.statusCode} />
            </Box>
          )}

          {serviceReport?.statusCode === ServiceReportStatusCode.inProgress ? (
            <TableActionButton>
              <Box>
                <MenuItem onClick={() => handleDownloadPdf(serviceReport)}>
                  <DownloadIcon width={18} height={18} />
                  {translate("downloadPdf")}
                </MenuItem>

                {canWrite && (
                  <MenuItem onClick={() => handleEditServiceReport(serviceReport)}>
                    <EditOutlinedIcon />
                    {tCommon("buttons.edit")}
                  </MenuItem>
                )}

                {canWrite && (
                  <MenuItem onClick={() => handleMarkAsSolved(serviceReport)}>
                    <PaperWithCrossIcon />
                    {translate("markAsSolved")}
                  </MenuItem>
                )}

                {canAdmin && (
                  <MenuItem
                    onClick={() =>
                      confirm({
                        title: translate("deleteServiceReport"),
                        text: translate("deleteConfirm", { number: serviceReport?.number }),
                        confirmButton: translate("yesDelete"),
                        closeButton: tCommon("common.no"),
                        severity: "error",
                        onConfirm: () => handleDeleteServiceReport(serviceReport)
                      })
                    }
                  >
                    <DeleteOutlinedIcon />
                    {translate("deleteServiceReport")}
                  </MenuItem>
                )}
              </Box>
            </TableActionButton>
          ) : (
            <TableActionButton>
              {serviceReport && (
                <MenuItem onClick={() => handleDownloadPdf(serviceReport)}>
                  <DownloadIcon width={18} height={18} />
                  {translate("downloadPdf")}
                </MenuItem>
              )}
            </TableActionButton>
          )}
        </Box>

        <Box className="flex flex-col gap-4 mt-4">
          <Box className="text-16-medium" color="#333333">
            {serviceReport?.title}
          </Box>

          <Box>
            <Box className="text-14-medium" color={colors.secondary.gray70}>
              {translate("public:componentAffected")}
            </Box>
            <Box className="text-16-medium" color="#333333">
              {partName}
            </Box>
          </Box>

          {serviceReport?.alarmCode && (
            <Box>
              <Box className="text-14-medium" color={colors.secondary.gray70}>
                {translate("public:alarmCode")}
              </Box>
              <Box className="text-16-medium" color="#333333">
                {serviceReport.alarmCode}
              </Box>
            </Box>
          )}

          {serviceReport?.urgency != null && URGENCY_MAP[serviceReport.urgency] && (
            <Box>
              <Box className="text-14-medium" color={colors.secondary.gray70}>
                {translate("public:urgency")}
              </Box>
              <Chip
                label={translate(`public:${URGENCY_MAP[serviceReport.urgency].label}`)}
                size="small"
                sx={{
                  backgroundColor: URGENCY_MAP[serviceReport.urgency].color,
                  color: "#fff",
                  fontWeight: 600,
                  mt: 0.5,
                }}
              />
            </Box>
          )}

          <Box>
            <Box className="text-14-medium" color={colors.secondary.gray40}>
              {translate("public:reportDescription")}
            </Box>
            <Box className="text-16-regular" color="#333333">
              {serviceReport?.description}
            </Box>
          </Box>

          {serviceReport?.notes && (
            <Box>
              <Box className="text-14-medium" color={colors.secondary.gray70}>
                {translate("public:technicalNotes")}
              </Box>
              <Box className="text-16-regular" color="#333333">
                {serviceReport.notes}
              </Box>
            </Box>
          )}

          {serviceReport && serviceReport?.files.length > 0 && (
            <Box className="mt-2">
              <Box className="flex flex-col gap-2">
                {reportFiles?.map((file, i) => (
                  <ReportFile key={i} file={file} canDelete={false} canDownload={true} fileIndex={i} />
                ))}
              </Box>
            </Box>
          )}

          {serviceReport?.statusCode === ServiceReportStatusCode.solved && (
            <Box className="flex flex-col gap-4">
              <Box className="heading-2 text-center">
                {translate("solvedReportTotal")} {"\u00A0"}
                <PriceFormat value={serviceReport?.cost?.value} />
              </Box>

              <Box>
                <Box className="text-14-medium" color={colors.secondary.gray40}>
                  {tCommon("description")}
                </Box>
                <Box className="text-16-regular" color="#333333">
                  {serviceReport?.solvingDescription}
                </Box>
              </Box>

              {serviceReport && serviceReport?.files.length > 0 && (
                <Box className="w-full">
                  <Box className="flex flex-col gap-2">
                    {solvedReportFiles?.map((file, i) => (
                      <ReportFile key={i} file={file} canDelete={false} canDownload={true} fileIndex={i} />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <KioskBackButton />
    </Box>
  );
};

export default ServiceReportPage;